import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, ApiError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

interface WorkReport {
  id: string;
  user_id: string;
  site_name: string;
  work_type: string;
  work_description: string;
  worker_count: number;
  work_hours: number;
  region: string;
  weather: string;
  work_date: string;
  remarks: string;
  created_at: string;
  updated_at: string;
  image_urls?: string[];
  profile?: { name: string; role: string } | null;
}

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [report, setReport] = useState<WorkReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    async function fetchReport() {
      setLoading(true);
      setFetchError("");
      try {
        const data = await api.get<WorkReport>(`/reports/${id}`);
        setReport(data);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setFetchError("일보를 찾을 수 없습니다.");
        } else {
          setFetchError("일보를 불러오는 중 오류가 발생했습니다.");
        }
      } finally {
        setLoading(false);
      }
    }

    if (id) fetchReport();
  }, [id]);

  const handleDelete = useCallback(async () => {
    if (!id) return;
    setDeleteError("");
    setDeleting(true);

    try {
      await api.del(`/reports/${id}`);

      setShowDeleteConfirm(false);
      setSuccessMsg("작업일보가 삭제되었습니다.");
      setTimeout(() => {
        navigate("/reports");
      }, 600);
    } catch {
      setDeleteError("삭제 중 오류가 발생했습니다.");
      setDeleting(false);
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">일보 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (fetchError || !report) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-gray-300 text-2xl"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">일보를 찾을 수 없습니다</h2>
        <p className="text-sm text-gray-400 mb-4">{fetchError || "해당 작업일보가 존재하지 않거나 삭제되었습니다."}</p>
        <button onClick={() => navigate("/reports")} className="text-sm text-amber-600 hover:text-amber-700 cursor-pointer">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  const weatherIcons: Record<string, string> = {
    "맑음": "ri-sun-line",
    "흐림": "ri-cloudy-line",
    "비": "ri-showers-line",
    "눈": "ri-snowy-line",
    "강풍": "ri-windy-line",
    "구름조금": "ri-cloudy-2-line"
  };

  const isOwner = user?.id === report.user_id;

  return (
    <div className="max-w-3xl pb-24">
      {successMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 bg-emerald-500 text-white rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium animate-[fadeIn_0.3s_ease-out]">
          <i className="ri-check-line text-lg"></i>
          {successMsg}
        </div>
      )}

      <div className="mb-6">
        <button onClick={() => navigate("/reports")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 cursor-pointer mb-3">
          <i className="ri-arrow-left-line"></i>
          작업일보 목록
        </button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">작업일보 상세</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {report.work_date} · {report.profile?.name || "알 수 없음"}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.site_name}</h2>
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {report.work_date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line"></i>
                {report.region}
              </span>
              <span className="flex items-center gap-1">
                <i className={weatherIcons[report.weather] || "ri-cloud-line"}></i>
                {report.weather}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              {report.work_type}
            </span>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">투입 인원</p>
              <p className="text-xl font-bold text-gray-900">{report.worker_count}<span className="text-sm font-normal text-gray-400">명</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">작업 시간</p>
              <p className="text-xl font-bold text-gray-900">{report.work_hours}<span className="text-sm font-normal text-gray-400">시간</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs text-gray-400 mb-1">작성자</p>
              <p className="text-lg font-semibold text-gray-900">{report.profile?.name || "알 수 없음"}</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">작업 내용</h3>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
              {report.work_description}
            </p>
          </div>

          {report.image_urls && report.image_urls.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                현장 사진 <span className="text-gray-400 font-normal">({report.image_urls.length}장)</span>
              </h3>
              <div className="flex flex-wrap gap-3">
                {report.image_urls.map((url, index) => (
                  <a
                    key={index}
                    href={resolveImageUrl(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-28 h-28 rounded-lg overflow-hidden border border-gray-200 hover:border-amber-400 transition-colors cursor-pointer"
                  >
                    <img
                      src={resolveImageUrl(url)}
                      alt={`현장사진 ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {report.remarks && (
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">특이사항</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap bg-amber-50 rounded-lg p-4 border border-amber-100">
                {report.remarks}
              </p>
            </div>
          )}

          <div className="border-t border-gray-100 pt-6 mt-6 flex items-center justify-between text-xs text-gray-400 flex-wrap gap-2">
            <span>작성일시: {new Date(report.created_at).toLocaleString("ko-KR")}</span>
            <span>수정일시: {new Date(report.updated_at).toLocaleString("ko-KR")}</span>
          </div>
        </div>
      </div>

      {/* 하단 고정 액션바 */}
      {isOwner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 py-3 z-40 flex items-center justify-center gap-3">
          <button
            onClick={() => navigate(`/report/${report.id}/edit`)}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-edit-line"></i>
            수정하기
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-6 py-2.5 border border-red-200 text-red-500 rounded-lg text-sm font-semibold hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-delete-bin-line"></i>
            삭제하기
          </button>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">작업일보 삭제</h3>
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center">
              <p className="text-sm font-medium text-gray-900">{report.site_name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{report.work_date} · {report.work_type}</p>
            </div>
            <p className="text-sm text-gray-500 text-center mb-4">
              이 작업일보를 영구적으로 삭제하시겠습니까?<br />이 작업은 되돌릴 수 없습니다.
            </p>
            {deleteError && (
              <p className="text-sm text-red-500 text-center mb-4 bg-red-50 rounded-lg py-2">{deleteError}</p>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(""); }}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    삭제 중...
                  </span>
                ) : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}